/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';

const SHIPPING_METHODS = [
    {
        name: 'Economy Shipping',
        cost: '$12.95',
        transitTime: '7–10 business days',
        processing: 'Same day if ordered before noon ET; next business day if ordered after noon ET',
    },
    {
        name: 'Standard Shipping',
        cost: '$15.95',
        transitTime: '3–7 business days',
        processing: 'Same day if ordered before noon ET; next business day if ordered after noon ET',
    },
    {
        name: 'Expedited Shipping',
        cost: '$34.95',
        transitTime: '2–3 business days',
        processing: 'Same day if ordered before noon ET; next business day if ordered after noon ET',
    },
    {
        name: 'Rush Shipping',
        cost: '$42.95',
        transitTime: '1–2 business days',
        processing: 'Same day if ordered Mon–Fri before noon ET',
    },
];

function Section({ title, children }: { title: string; children: ReactElement | ReactElement[] | string }) {
    return (
        <div className="mb-10">
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-2xl font-light mb-4 text-foreground">
                {title}
            </h2>
            <div className="text-muted-foreground leading-relaxed text-sm space-y-3">{children}</div>
        </div>
    );
}

export default function ShippingMethodsAndCosts(): ReactElement {
    return (
        <div className="pb-16">
            <SeoMeta
                title="Shipping Methods &amp; Costs — Baggallini"
                description="Learn about Baggallini shipping options, costs, and delivery timelines for Economy, Standard, Expedited, and Rush shipping to the US including Alaska, Hawaii, and APO/FPO addresses."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Shipping Methods &amp; Costs</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <div className="max-w-screen-md mx-auto px-6 pt-10">
                <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-5xl font-light tracking-wide mb-3">
                    Shipping Methods &amp; Costs
                </h1>
                <p className="text-muted-foreground mb-10 max-w-xl">
                    We ship to the contiguous 48 states, District of Columbia, Alaska, Hawaii, U.S. Territories, and
                    APO/FPO/DPO addresses. We do not ship to Canada or internationally.
                </p>

                {/* Quick summary card */}
                <div className="bg-[#F5F3F0] rounded-2xl px-8 py-6 mb-10 grid sm:grid-cols-3 gap-6 text-center">
                    {[
                        { stat: 'Free', label: 'shipping on orders $100+' },
                        { stat: '1–2 Days', label: 'fastest delivery option' },
                        { stat: 'Same Day', label: 'processing before noon ET' },
                    ].map((item) => (
                        <div key={item.stat}>
                            <p
                                style={{ fontFamily: 'var(--font-display)' }}
                                className="text-3xl font-light mb-1 text-foreground">
                                {item.stat}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase tracking-[0.08em]">{item.label}</p>
                        </div>
                    ))}
                </div>

                <Section title="Shipping Options">
                    <p>
                        Choose from four shipping methods at checkout. All orders are processed on business days
                        (Monday–Friday, excluding holidays).
                    </p>

                    {/* Shipping methods table */}
                    <div className="overflow-x-auto mt-4">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-[#e8e8e8]">
                                    <th className="text-left py-3 pr-4 font-medium" style={{ color: '#1a1a1a' }}>
                                        Method
                                    </th>
                                    <th className="text-left py-3 pr-4 font-medium" style={{ color: '#1a1a1a' }}>
                                        Cost
                                    </th>
                                    <th className="text-left py-3 font-medium" style={{ color: '#1a1a1a' }}>
                                        Delivery Time
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {SHIPPING_METHODS.map((method) => (
                                    <tr key={method.name} className="border-b border-[#e8e8e8]">
                                        <td className="py-3 pr-4 font-medium" style={{ color: '#1a1a1a' }}>
                                            {method.name}
                                        </td>
                                        <td className="py-3 pr-4" style={{ color: '#5a5a5a' }}>
                                            {method.cost}
                                        </td>
                                        <td className="py-3" style={{ color: '#5a5a5a' }}>
                                            {method.transitTime}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p className="text-xs bg-[#F5F3F0] border border-[#E0DDD8] rounded-lg px-4 py-3 text-foreground/70 mt-2">
                        <strong>Processing:</strong> Orders placed before 12:00 PM ET ship same day. Orders placed after
                        12:00 PM ET ship the next business day. Delivery estimates exclude weekends and holidays.
                    </p>
                </Section>

                <Section title="Free Shipping">
                    <p>
                        Enjoy <strong className="text-foreground">free shipping</strong> on orders of{' '}
                        <strong className="text-foreground">$100 or more</strong> when the promotional offer is active.
                        Look for the free shipping banner on our homepage and at checkout.
                    </p>
                </Section>

                <Section title="Alaska, Hawaii &amp; U.S. Territories">
                    <p>
                        In-stock items shipped to Alaska, Hawaii, and U.S. Territories typically arrive within{' '}
                        <strong className="text-foreground">7–10 business days</strong>.
                    </p>
                </Section>

                <Section title="APO / DPO / FPO Addresses">
                    <p>
                        We ship to APO, DPO, and FPO addresses via USPS. In-stock items typically arrive within{' '}
                        <strong className="text-foreground">21 business days</strong>. Expedited delivery is not
                        available for military addresses.
                    </p>
                </Section>

                {/* Help strip */}
                <div className="border-t border-[#e8e8e8] pt-8 mt-4 flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-foreground mb-1">Questions about your order?</p>
                        <p className="text-sm text-muted-foreground">
                            Our team is available Mon–Fri, 9am–5pm ET at{' '}
                            <a href="tel:18004488753" className="text-foreground hover:underline">
                                1-800-448-8753
                            </a>
                            .
                        </p>
                    </div>
                    <div className="flex gap-3 shrink-0">
                        <Link
                            to="/returns"
                            className="text-sm border border-foreground/30 rounded-full px-4 py-1.5 hover:bg-foreground hover:text-background transition-colors">
                            Returns
                        </Link>
                        <Link
                            to="/contact-us"
                            className="text-sm bg-foreground text-background rounded-full px-4 py-1.5 hover:opacity-80 transition-opacity">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
